import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Box, Divider, IconButton, InputAdornment, TextField, Typography, useMediaQuery } from '@mui/material'
import { AppleButton } from '~/components/common/AppleButton'
import { GoogleButton } from '~/components/common/GoogleButton'
import { MarketingButton } from '~/components/common/MarketingButton'
import { SigninSignupContent } from '~/components/common/SigninSignupContent'
import { TermsCheckbox } from '~/components/common/TermsCheckbox'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { pagesPath } from '~/lib/$path'

import { EMAIL_FORMAT_REGEXP } from '~/lib/constants'

import { FirebaseError } from 'firebase/app'
import { auth } from '~/lib/firebase/auth'
import { firebaseRegLoginProvider, firebaseSigInProvider } from '~/lib/firebase/firebaseUserCheck'
import { eventPush } from '~/lib/gtmHelper'
import { snackActions } from '~/lib/snackbarHelper'
import { theme } from '~/lib/theme'
import { logger } from '~/lib/logger'

type FieldValues = { email: string; password: string; term: boolean }

export const SignInPage = () => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FieldValues>({ defaultValues: { email: '', password: '', term: false } })
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)
  const router = useRouter()

  const withCleanup = async (authMethod: () => Promise<unknown>): Promise<unknown> => {
    await auth.signOutWithCleanup()
    return authMethod()
  }

  const handleGoogleSignIn = () => {
    withCleanup(() => auth.signInWithGoogle()).catch(() => snackActions.error('Google認証が失敗しました'))
  }

  const handleAppleSignIn = () => {
    withCleanup(() => auth.signInWithApple()).catch(() => snackActions.error('Apple認証が失敗しました'))
  }

  const isBreakpointUpMd = useMediaQuery(theme.breakpoints.up('md'))

  return (
    <SigninSignupContent>
      <form
        style={{ height: '100%' }}
        onSubmit={handleSubmit(async (data) => {
          const loginProvider = await firebaseRegLoginProvider(data.email)
          if (loginProvider.length && !loginProvider.includes(firebaseSigInProvider.password)) {
            const createErrorText = loginProvider.map((provider) => {
              switch (provider) {
                case firebaseSigInProvider.apple:
                  return 'Appleログイン'
                case firebaseSigInProvider.google:
                  return 'Googleログイン'
              }
            })

            const errorText = createErrorText.join('、')
            snackActions.error(`このメールアドレスは${errorText}で使用されています`)
            return
          }

          try {
            await withCleanup(() => auth.signInWithEmailAndPassword(data.email, data.password))

            eventPush('login', {
              method: 'email',
            })
            // ページ遷移はログインしたUserAdvに依存するためpages/sigin/index.tsx側でまとめて行う
          } catch (error) {
            if (error instanceof FirebaseError) {
              if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                snackActions.error('メールアドレスまたはパスワードが間違っています')
                return
              }
              logger.error(new Error('Failed to sign in', { cause: error }))
              snackActions.error('認証に失敗しました')
            }
          }
        })}
      >
        <Box display="flex" flexDirection="column" alignItems="stretch" minHeight="100%" justifyContent="center">
          <Box sx={{ height: { xs: 50, md: 72 } }} />
          <Typography variant="headingM" align="center" sx={{ fontWeight: 'bold', px: 4, wordBreak: 'keep-all' }}>
            {isBreakpointUpMd ? 'toridori marketing へようこそ' : 'ログイン'}
          </Typography>
          <Box height={32} />
          <Box width="60%" alignSelf="center" display="flex" flexDirection="column" alignItems="stretch">
            <Typography variant="caption">メールアドレスでログイン</Typography>

            <Box height={8} />
            {/* メールアドレス入力フォーム */}
            <Controller
              name="email"
              rules={{
                required: 'メールアドレスは必須です。',
                pattern: {
                  value: EMAIL_FORMAT_REGEXP,
                  message: 'user@example.com の形式で入力してください。',
                },
              }}
              control={control}
              render={({ field }) => (
                <TextField
                  type="email"
                  variant="outlined"
                  error={!!errors.email}
                  label={'メールアドレス'}
                  fullWidth
                  helperText={errors.email?.message}
                  {...field}
                />
              )}
            />
            <Box height={12} />
            {/* パスワード入力フォーム */}
            <Controller
              name="password"
              rules={{
                required: 'パスワードは必須です。',
              }}
              control={control}
              render={({ field }) => (
                <TextField
                  error={!!errors.password}
                  type={isPasswordVisible ? 'text' : 'password'}
                  fullWidth
                  helperText={errors.password?.message}
                  label="パスワード"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          name="password-visibility"
                          aria-label="パスワード表示/非表示"
                          size="small"
                          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {isPasswordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...field}
                />
              )}
            />
            <Box height={16} />

            {/* 利用規約チェックボックス */}
            <Box mt={1} display="flex" justifyContent="center">
              <TermsCheckbox control={control} onChange={(checked) => setButtonDisabled(!checked)} />
            </Box>

            <Box height={16} />
            {/* ログインボタン */}
            <MarketingButton
              name="signin"
              aria-label="ログイン"
              variant="contained"
              type="submit"
              disabled={buttonDisabled}
              loading={isSubmitting}
            >
              ログイン
            </MarketingButton>

            <Box height={10} />
            {/* パスワードリセット */}
            <MarketingButton variant="text" onClick={() => router.push(pagesPath.reset_password.$url())}>
              パスワードを忘れた方はこちら
            </MarketingButton>

            <Box height={28} />

            <GoogleButton
              name="google-signup"
              aria-label="Googleでログイン"
              value="Googleでログイン"
              onClick={handleGoogleSignIn}
              disabled={buttonDisabled}
              fullWidth
            />
            <Box height={10} />
            <AppleButton
              name="apple-signin"
              aria-label="Appleでログイン"
              value="Appleでログイン"
              onClick={handleAppleSignIn}
              disabled={buttonDisabled}
              fullWidth
            />

            <Box height={14} />
            <Typography variant="overline">
              Googleログイン及びAppleログインに使用したメールアドレスはメールアドレスでログインに使用できなくなります。ご注意ください
            </Typography>
            <Box height={7} />
          </Box>

          <Box height={16} />
          <Box width="80%" alignSelf="center">
            <Divider light />
          </Box>
          <Box height={18} />
          <Box width="80%" alignSelf="center">
            <MarketingButton
              variant="outlined"
              onClick={() => {
                eventPush('start_sign_up', {})
                router.push('/signup')
              }}
              fullWidth
            >
              会員登録に進む
            </MarketingButton>
            <Box height={48} />
          </Box>
        </Box>
      </form>
    </SigninSignupContent>
  )
}
